import React, { Fragment, useEffect, useRef, useState } from "react";
import { classNames } from "../components/common/Helpers";
import { Combobox } from "@headlessui/react";
import get from "lodash/get";
import { BiSolidPencil } from "react-icons/bi";
import { PulseLoader } from "react-spinners";

export default function PaymentInput(
    {
        label,
        type,
        name,
        value,
        placeholder,
        inputDescription,
        required,
        onChange,
        onInput,
        autoFocus,
        horizontal,
        containerClass,
        labelClass,
        inputClass,
        renderBeforeInput,
        autoComplete = {},
        error,
        onClearError,
    }
) {
    const [input, setInput] = useState('')
    const [inputFocus, setInputFocus] = useState(false)
    const comboboxInput = useRef();
    const comboboxBtn = useRef();
    const [open, setOpen] = useState(false);
    autoComplete = {
        ...{
            idKey: 'id',
            labelKey: 'name',
            secondaryKey: null,
            imageKey: null,
            showLoading: true,
            search: null,
        },
        ...autoComplete,
    };

    const loading = autoComplete?.loading;
    const options = autoComplete?.results ?? [];

    useEffect(() => {
        if (!autoComplete.search) return;
        if (!inputFocus && !open) {
            updateSelection();
        }
    }, [autoComplete.search, inputFocus, open]);

    useEffect(() => {
        if (error && input?.length > 0) {
            onClearError();
        }
        if (!autoComplete.search || input?.length < 2) return;
        autoComplete.search(input)
    }, [input]);

    const updateSelection = () => {
        if (input?.trim() === value?.[autoComplete.labelKey]?.trim()) {
            return;
        }

        const option = options.find((o) => o[autoComplete.labelKey]?.trim() === input?.trim());
        if (option) {
            onChange(option);
            return;
        }

        onChange({ [autoComplete.idKey]: null, [autoComplete.labelKey]: input })
    };

    return (
        <div
            className={classNames(
                " flex",
                horizontal ? 'flex-row' : 'flex-col',
                containerClass
            )}
        >
            <label
                className={classNames(
                    "block mb-[0.25rem] text-[0.93rem] leading-[1.15] text-[#30313d]",
                    labelClass
                )}
                htmlFor={"input-" + name}
            >
                {label}
            </label>

            {!autoComplete.search ? (
                <div className="relative">
                    <input
                        id={"input-" + name}
                        className={classNames(
                            !inputDescription ? "p-3" : "pt-1 pb-[1.45rem]",
                            "border shadow shadow-gray-100/70 rounded-[5px] leading-3",
                            "shadow-[rgba(0,0,0,0.03)_0px_1px_1px,rgba(0,0,0,0.02)_0px_3px_6px]",
                            "appearance-none bg-transparent outline-none focus:ring-0 w-full",
                            "focus:border-blue-600 focus:ring-[3px] focus:ring-blue-200 transition-all",
                            type === 'number' && '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                            !error ? 'border-gray-300/50' : 'border-red-600 ring-[1px] ring-red-600',
                            inputClass
                        )}
                        required={required}
                        type={type}
                        name={name}
                        onChange={(e) => {
                            setInput(e.target.value);
                            onChange(e);
                        }}
                        placeholder={placeholder}
                        onInput={onInput}
                        autoFocus={autoFocus}
                    />
                    {inputDescription && <p className="absolute bottom-1.5 left-3 text-sm italic text-gray-500">{inputDescription}</p>}
                </div>
            ) : (
                <div className="relative">
                    <Combobox
                        value={value}
                        onChange={(v) => {
                            onChange(v);
                            setInput(v?.[autoComplete.labelKey]);
                            comboboxInput.current?.blur();
                        }}
                    >
                        {({ open }) => {
                            useEffect(() => {
                                setOpen(open);
                            }, [open]);

                            return (
                                <>
                                    <div className="relative">
                                        {renderBeforeInput && renderBeforeInput({ open, focused: inputFocus })}

                                        {!open && autoComplete.imageKey && value?.[autoComplete.idKey] && (
                                            <img
                                                src={get(value, autoComplete.imageKey) || 'https://manage2.tukioswebsites.com/images/no-image.jpg'}
                                                alt={value[autoComplete.labelKey]}
                                                className="absolute top-[9px] left-2.5 w-7 h-7 rounded-full mr-2 object-cover object-center"
                                            />
                                        )}

                                        {!open && value?.[autoComplete.labelKey] && (
                                            <BiSolidPencil
                                                size={16}
                                                className="absolute right-3 top-3.5 pointer-events-none text-slate-500"
                                            />
                                        )}

                                        {open && loading && autoComplete.showLoading && (
                                            <div className="absolute top-2 right-0 px-3.5">
                                                <PulseLoader size={2} color={"#b7d9ff"} />
                                            </div>
                                        )}

                                        <Combobox.Button ref={comboboxBtn} />
                                        <Combobox.Input
                                            id={"input-" + name}
                                            ref={comboboxInput}
                                            onClick={() => !open && comboboxBtn.current?.click()}
                                            onChange={(e) => setInput(e.target.value)}
                                            onFocus={() => setInputFocus(true)}
                                            onBlur={() => setInputFocus(false)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && options?.length === 0) {
                                                    onChange({
                                                        [autoComplete.idKey]: null,
                                                        [autoComplete.labelKey]: input
                                                    })
                                                    e.preventDefault();
                                                }
                                            }}
                                            className={classNames(
                                                "px-3.5 py-2.5",
                                                !open && value?.[autoComplete.idKey] && "pl-12",
                                                !open && value?.[autoComplete.labelKey] && "cursor-pointer",
                                                open && loading && "pr-11",
                                                "border shadow-md shadow-gray-100/70 rounded-md",
                                                "appearance-none bg-transparent outline-none focus:ring-0 w-full",
                                                "focus:border-blue-600 focus:ring-[3px] focus:ring-blue-200 transition-all",
                                                type === 'number' && '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                                                !error ? 'border-gray-300/50' : 'border-red-600 ring-[1px] ring-red-600',
                                                inputClass
                                            )}
                                            name={name}
                                            placeholder={placeholder}
                                            onInput={onInput}
                                            autoFocus={autoFocus}
                                            autoComplete="off"
                                            displayValue={(v) => v?.[autoComplete.labelKey]}
                                        />
                                    </div>

                                    {open && options?.length > 0 && (
                                        <Combobox.Options
                                            className="absolute bg-white w-full z-100 rounded-lg shadow border mt-2 overflow-hidden">
                                            {options.map((option, index) => (
                                                <Combobox.Option
                                                    key={option[autoComplete.idKey]}
                                                    value={option}
                                                    as={Fragment}
                                                >
                                                    {({ active, selected }) => (
                                                        <li
                                                            className={classNames(
                                                                "flex items-center cursor-pointer p-2",
                                                                active && "bg-blue-50",
                                                            )}
                                                        >
                                                            {autoComplete.imageKey && (
                                                                <img
                                                                    src={get(option, autoComplete.imageKey) || 'https://manage2.tukioswebsites.com/images/no-image.jpg'}
                                                                    alt={option[autoComplete.labelKey]}
                                                                    className="w-8 h-8 rounded-full mr-2 object-cover object-center"
                                                                />
                                                            )}
                                                            <div>
                                                                <p>{option[autoComplete.labelKey]}</p>
                                                                {autoComplete.secondaryKey && (
                                                                    <p className="text-sm text-gray-500">{option[autoComplete.secondaryKey]}</p>
                                                                )}
                                                            </div>
                                                        </li>
                                                    )}
                                                </Combobox.Option>
                                            ))}
                                        </Combobox.Options>
                                    )}
                                </>
                            );
                        }}
                    </Combobox>
                </div>
            )}

            {error && (
                <p className="text-red-600 text-[0.93rem] mt-1">
                    {error}
                </p>
            )}
        </div>
    )
}
